import { Product, ProductTrackingType } from '../../../clients/quipli-service/product/product';
import { ProductImage } from '../../../components/image/product/product-image';
import classNames from 'classnames';
import { calcUnitRowHeight, ProductRowHeight, RangeUnit, UnitRowHeight } from './inventory-gantt';
import { ProductCalendar, UnitCalendar, Utilization } from '../../../clients/quipli-service/availability/availability';
import { Tooltip } from '../../../components/tooltip/tooltip';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Category } from '../../../clients/quipli-service/category/category';
import { Location } from '../../../clients/quipli-service/location/location';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import { ProductImageV2 } from '../../../components/image/product/product-image-v2';
import { Interval } from 'luxon';

export const ProductList = ({
  products,
  categories,
  locations,
  expandedCategories,
  setExpandedCategories,
  productCalendars,
  expandedProducts,
  setExpandedProducts,
  productRowHeights,
  rangeUnit,
  registerScrollNode,
  serialNumberEnabled,
  isDashboard = true,
}: {
  products: Product[];
  categories: Category[] | undefined;
  locations: Location[];
  expandedCategories: number[];
  setExpandedCategories: (arr: number[]) => void;
  productCalendars: { calendar: ProductCalendar[] | undefined; range: Interval }[];
  expandedProducts: number[];
  setExpandedProducts: (arr: number[]) => void;
  productRowHeights: ProductRowHeight[];
  rangeUnit: RangeUnit;
  registerScrollNode: (n: HTMLOListElement) => void;
  serialNumberEnabled: boolean;
  isDashboard?: boolean;
}) => {
  const [productImagesV2, setProductImagesV2] = useState<boolean>(true);
  const scrollRef = useRef<HTMLOListElement>(null);

  function setExpandedProduct(productId: number) {
    return (isExpanded: boolean) => {
      if (isExpanded) {
        setExpandedProducts([...expandedProducts, productId]);
      } else {
        setExpandedProducts(expandedProducts.filter((id) => productId !== id));
      }
    };
  }

  function setExpandedCategory(categoryId: number) {
    return (isExpanded: boolean) => {
      if (isExpanded) {
        setExpandedCategories([...expandedCategories, categoryId]);
      } else {
        setExpandedCategories(expandedCategories.filter((id) => categoryId !== id));
      }
    };
  }

  useEffect(() => {
    if (scrollRef.current) {
      registerScrollNode(scrollRef.current);
    }
  }, [scrollRef]);

  const { getConfiguration } = useCustomerAPI();
  useEffect(() => {
    getConfiguration('CONF_PRODUCT_IMAGES_V2').then((config) => {
      setProductImagesV2(config?.value === '1');
    });
  }, []);

  function renderProducts(ps: Product[]): ReactNode {
    return ps.map((p) => (
      <ProductContainer
        key={p.id}
        product={p}
        locations={locations}
        productCalendars={productCalendars.map((pcs) => pcs.calendar?.find((pc) => pc.product.id === p.id))}
        isExpanded={expandedProducts.indexOf(p.id) >= 0}
        setExpanded={setExpandedProduct(p.id)}
        rowHeights={productRowHeights.find((prh) => prh.product === p.id)?.unitHeights || []}
        rangeUnit={rangeUnit}
        serialNumberEnabled={serialNumberEnabled}
        productImagesV2={productImagesV2}
      />
    ));
  }

  function renderCategory(c: Category): ReactNode {
    const isExpanded = expandedCategories.indexOf(c.id) >= 0;
    return (
      <div key={c.id} className="category-row">
        <div
          className={classNames('category-name', {
            expanded: isExpanded,
          })}
          onClick={() => setExpandedCategory(c.id)(!isExpanded)}
        >
          <i className={classNames('fa-solid', { 'fa-angle-down': !isExpanded, 'fa-angle-up': isExpanded })} />
          <div>{c.name}</div>
        </div>

        {isExpanded && renderProducts(products.filter((p) => p.categoryIds.indexOf(c.id) >= 0))}
      </div>
    );
  }

  return (
    <ol ref={scrollRef} className={classNames('product-list', isDashboard ? '' : 'max-h-[75vh]')}>
      <li className="product product-row vertical-center header">
        <div className="product-name items-center">Products</div>
      </li>
      {categories ? categories.map(renderCategory) : renderProducts(products)}
    </ol>
  );
};

const UtilizationWidget = ({
  calendars,
  rangeUnit,
}: {
  calendars: (UnitCalendar | ProductCalendar | undefined)[];
  rangeUnit: RangeUnit;
}) => {
  function utilizationSpan(key: number, u: Utilization) {
    const pct = u.utilization * 100;
    let prefix;

    if (rangeUnit === 'day') {
      switch (key) {
        case 0:
          prefix = "Yesterday's";
          break;
        case 1:
          prefix = "Today's";
          break;
        case 2:
          prefix = "Tomorrow's";
      }
    } else {
      switch (key) {
        case 0:
          prefix = `Previous ${rangeUnit}'s`;
          break;
        case 1:
          prefix = `Current ${rangeUnit}'s`;
          break;
        case 2:
          prefix = `Next ${rangeUnit}'s`;
      }
    }

    const tooltip = <div>{prefix} utilization</div>;
    return (
      <Tooltip tooltip={tooltip}>
        <span
          className={classNames({
            high: pct >= u.utilizationThresholdHigh,
            normal: pct >= u.utilizationThresholdLow && pct < u.utilizationThresholdHigh,
            low: pct < u.utilizationThresholdLow,
          })}
        >
          {pct.toFixed(1)}%
        </span>
      </Tooltip>
    );
  }

  return (
    <div className="utilization">
      {calendars.map((calendar, i) => (
        <div key={i}>{calendar ? (calendar.utilization ? utilizationSpan(i, calendar.utilization) : '-') : '-'}</div>
      ))}
    </div>
  );
};

const ProductContainer = ({
  product,
  locations,
  productCalendars,
  isExpanded,
  setExpanded,
  rowHeights,
  rangeUnit,
  serialNumberEnabled,
  productImagesV2,
}: {
  product: Product;
  locations: Location[];
  productCalendars?: (ProductCalendar | undefined)[];
  isExpanded: boolean;
  setExpanded: (b: boolean) => void;
  rowHeights: UnitRowHeight[];
  rangeUnit: RangeUnit;
  serialNumberEnabled: boolean;
  productImagesV2: boolean;
}) => {
  const unitTrackingField = serialNumberEnabled ? 'serialNumber' : 'unitNumber';
  return (
    <li className="product">
      <div
        className={classNames('product-row', {
          expanded: isExpanded,
        })}
        onClick={() => setExpanded(!isExpanded)}
      >
        <div className="product-name">
          <div className="vertical-center">
            <i className={classNames('fa-solid', { 'fa-angle-down': !isExpanded, 'fa-angle-up': isExpanded })} />
          </div>
          <div className="image-container vertical-center">
            {productImagesV2 ? (
              <ProductImageV2 productId={product.id} profile="MINI" />
            ) : (
              <ProductImage productId={product.id} profile="MINI" />
            )}
          </div>
          <div className="vertical-center product-name-label">
            <div title={product.name}>{product.name}</div>
            {productCalendars && <UtilizationWidget calendars={productCalendars} rangeUnit={rangeUnit} />}
          </div>
        </div>
      </div>
      <ol className="unit-list">
        {isExpanded &&
          product.inventoryUnits
            .sort((a, b) => {
              if (a.locationId != b.locationId) {
                return (locations.find((l) => l.id === a.locationId)?.name || '').localeCompare(
                  locations.find((l) => l.id === b.locationId)?.name || '',
                );
              } else {
                return a[unitTrackingField].localeCompare(b[unitTrackingField]);
              }
            })
            .map((iu) => {
              const unitCalendars = productCalendars?.map((pc) => pc?.unitCalendar.find((uc) => uc.unit.id === iu.id));
              const location = locations.find((l) => iu.locationId === l.id);
              return (
                <li
                  key={iu.id}
                  className="unit-row"
                  style={{ height: calcUnitRowHeight(rowHeights.find((rh) => rh.unit === iu.id)?.height) }}
                >
                  <div className={classNames({ disabled: !iu.isActive }, 'unit-name')}>
                    <i className="fa fa-gear"></i>
                    <div>
                      <div title={iu[unitTrackingField]}>
                        {iu[unitTrackingField]} {!iu.isActive ? '(Inactive)' : iu.isOnOutage ? '(Outage)' : ''}{' '}
                        {locations.length > 1 ? (
                          <span
                            className={classNames(
                              'location-badge',
                              `_${
                                location
                                  ? (locations.sort((a, b) => a.name.localeCompare(b.name)).indexOf(location) % 2) + 1
                                  : 1
                              }`,
                            )}
                          >
                            {location?.name}
                          </span>
                        ) : (
                          ''
                        )}
                      </div>
                      {unitCalendars && <UtilizationWidget calendars={unitCalendars} rangeUnit={rangeUnit} />}
                    </div>
                  </div>
                </li>
              );
            })}
        {isExpanded && (
          <li
            className="unit-row"
            style={{ height: calcUnitRowHeight(rowHeights.find((rh) => rh.unit === -1)?.height) }}
          >
            <div className="unit-name">
              {product.trackingType === ProductTrackingType.SerialUnits && product.rentalStock > 0 ? (
                <>
                  <i className="fa fa-warning"></i>
                  <div>Unassigned</div>
                </>
              ) : (
                <>
                  <i className="fa fa-clipboard-list" />
                  <div>Orders</div>
                </>
              )}
            </div>
          </li>
        )}
      </ol>
    </li>
  );
};
